import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getCommerceId } from '@/lib/ownership';
import type { ZifekUser } from '@/hooks/useAuth';
import { repairAppCache, hardReload, isPwaSupported, isStandalone } from '@/lib/pwa';

interface Props {
  user: ZifekUser;
}

interface VisitsDiag {
  today: number;
  week: number;
  total: number;
  lastAt: string | null;
}

/**
 * Onglet « Réparer » des paramètres :
 *   • force la mise à jour du service worker + vide le cache local ;
 *   • vérifie le suivi des statistiques de fréquentation du site public
 *     (les visites remontent-elles bien après la correction ?).
 */
export default function MaintenanceSettings({ user }: Props) {
  // ── Réparation du cache ──
  const [repairing, setRepairing] = useState(false);
  const [repairMsg, setRepairMsg] = useState<string | null>(null);

  const handleRepair = useCallback(async () => {
    if (repairing) return;
    setRepairing(true);
    setRepairMsg(null);
    try {
      const result = await repairAppCache();
      setRepairMsg(
        `${result.message} ${result.clearedCaches > 0 ? `(${result.clearedCaches} cache${result.clearedCaches > 1 ? 's' : ''} vidé${result.clearedCaches > 1 ? 's' : ''})` : ''}`.trim(),
      );
      hardReload(900);
    } catch {
      setRepairMsg("Impossible de réparer le cache pour l'instant. Réessayez.");
      setRepairing(false);
    }
  }, [repairing]);

  // ── Forcer la mise à jour (vidage cache + activation de la nouvelle version) ──
  const [forcing, setForcing] = useState(false);
  const [forceMsg, setForceMsg] = useState<string | null>(null);

  const handleForceUpdate = useCallback(async () => {
    if (forcing) return;
    setForcing(true);
    setForceMsg(null);
    try {
      const result = await repairAppCache();
      setForceMsg(result.message);
      // Rechargement immédiat : l'utilisateur récupère la dernière version publiée.
      hardReload(700);
    } catch {
      setForceMsg("Impossible de forcer la mise à jour pour l'instant. Réessayez.");
      setForcing(false);
    }
  }, [forcing]);

  // ── Diagnostic fréquentation ──
  const commerceId = getCommerceId(user);
  const [diag, setDiag] = useState<VisitsDiag | null>(null);
  const [diagLoading, setDiagLoading] = useState(true);
  const [diagError, setDiagError] = useState<string | null>(null);

  const loadDiag = useCallback(async () => {
    setDiagLoading(true);
    setDiagError(null);
    try {
      const now = new Date();
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      const weekStart = new Date(todayStart);
      weekStart.setDate(weekStart.getDate() - 6);

      const bouNom = user.nomcommerce || '';

      const [todayRes, weekRes, totalRes, themeLastRes] = await Promise.all([
        supabase
          .from('visitesiteweb')
          .select('id', { count: 'exact', head: true })
          .eq('boutique_nom', bouNom)
          .gte('date', todayStart.toISOString()),
        supabase
          .from('visitesiteweb')
          .select('id', { count: 'exact', head: true })
          .eq('boutique_nom', bouNom)
          .gte('date', weekStart.toISOString()),
        supabase
          .from('visitesiteweb')
          .select('id', { count: 'exact', head: true })
          .eq('boutique_nom', bouNom),
        commerceId
          ? supabase
              .from('theme_page_visits')
              .select('created_at')
              .eq('boutique_id', commerceId)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);

      setDiag({
        today: todayRes.count || 0,
        week: weekRes.count || 0,
        total: totalRes.count || 0,
        lastAt: (themeLastRes.data as { created_at?: string } | null)?.created_at ?? null,
      });
    } catch {
      setDiagError('Impossible de lire les statistiques de fréquentation.');
    } finally {
      setDiagLoading(false);
    }
  }, [user.nomcommerce, commerceId]);

  useEffect(() => { loadDiag(); }, [loadDiag]);

  const trackingOk = (diag?.total ?? 0) > 0 || Boolean(diag?.lastAt);

  return (
    <div className="space-y-5">
      {/* ── Carte : Réparer / Vider le cache ── */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <h3 className="text-sm font-semibold text-foreground-700 mb-1 flex items-center gap-2">
          <i className="ri-refresh-line text-accent-500"></i>
          Réparer l&apos;application
        </h3>
        <p className="text-xs text-foreground-500 mb-4 leading-relaxed">
          Si votre site affiche une ancienne version figée, cliquez ci-dessous : la mise à jour est
          forcée, le cache local est vidé puis la page se recharge automatiquement.
        </p>

        <div className="flex items-center gap-2 flex-wrap mb-4">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100 text-[11px] font-medium text-foreground-600">
            <i className={isPwaSupported() ? 'ri-checkbox-circle-line text-accent-600' : 'ri-close-circle-line text-foreground-400'}></i>
            Service worker {isPwaSupported() ? 'actif' : 'indisponible'}
          </span>
          {isStandalone() && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-[11px] font-medium text-secondary-900">
              <i className="ri-smartphone-line"></i>Application installée
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleRepair}
          disabled={repairing}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
        >
          {repairing ? (
            <><i className="ri-loader-4-line animate-spin"></i>Réparation...</>
          ) : (
            <><i className="ri-loop-right-line"></i>Réparer / Vider le cache</>
          )}
        </button>

        {repairMsg && (
          <p className="mt-3 text-xs text-accent-700 flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line"></i>{repairMsg}
          </p>
        )}
      </div>

      {/* ── Carte : Forcer la mise à jour ── */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <h3 className="text-sm font-semibold text-foreground-700 mb-1 flex items-center gap-2">
          <i className="ri-download-cloud-2-line text-accent-500"></i>
          Forcer la mise à jour
        </h3>
        <p className="text-xs text-foreground-500 mb-4 leading-relaxed">
          Vide le cache de l&apos;application, récupère la dernière version publiée (nouvelles pages,
          correctifs) et recharge immédiatement la page. À utiliser quand une nouveauté n&apos;apparaît pas,
          surtout sur l&apos;application installée (Android et iPhone).
        </p>

        <div className="flex items-center gap-2 flex-wrap mb-4">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100 text-[11px] font-medium text-foreground-600">
            <i className={isPwaSupported() ? 'ri-checkbox-circle-line text-accent-600' : 'ri-close-circle-line text-foreground-400'}></i>
            Service worker {isPwaSupported() ? 'actif' : 'indisponible'}
          </span>
          {isStandalone() && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-[11px] font-medium text-secondary-900">
              <i className="ri-smartphone-line"></i>Application installée
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleForceUpdate}
          disabled={forcing}
          className="flex items-center gap-2 px-6 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-60"
        >
          {forcing ? (
            <><i className="ri-loader-4-line animate-spin"></i>Mise à jour...</>
          ) : (
            <><i className="ri-download-cloud-2-line"></i>Forcer la mise à jour</>
          )}
        </button>

        {forceMsg && (
          <p className="mt-3 text-xs text-accent-700 flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line"></i>{forceMsg}
          </p>
        )}
      </div>

      {/* ── Carte : Diagnostic fréquentation ── */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h3 className="text-sm font-semibold text-foreground-700 flex items-center gap-2">
            <i className="ri-line-chart-line text-accent-500"></i>
            Suivi de la fréquentation
          </h3>
          <button
            type="button"
            onClick={loadDiag}
            disabled={diagLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
          >
            {diagLoading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-refresh-line"></i>}
            Actualiser
          </button>
        </div>
        <p className="text-xs text-foreground-500 mb-4 leading-relaxed">
          Vérifiez que les visites de votre site public sont bien comptées. Les données se rafraîchissent
          après chaque visite de votre boutique.
        </p>

        {diagError ? (
          <p className="text-xs text-red-500 flex items-center gap-1.5">
            <i className="ri-error-warning-line"></i>{diagError}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-background-100 p-3">
                <div className="text-[11px] text-foreground-500 mb-1">Aujourd&apos;hui</div>
                <div className="text-lg font-bold font-heading text-foreground-950">
                  {diagLoading ? '…' : (diag?.today ?? 0).toLocaleString()}
                </div>
              </div>
              <div className="rounded-lg bg-background-100 p-3">
                <div className="text-[11px] text-foreground-500 mb-1">7 derniers jours</div>
                <div className="text-lg font-bold font-heading text-foreground-950">
                  {diagLoading ? '…' : (diag?.week ?? 0).toLocaleString()}
                </div>
              </div>
              <div className="rounded-lg bg-accent-100/70 p-3">
                <div className="text-[11px] text-accent-900/80 mb-1">Total</div>
                <div className="text-lg font-bold font-heading text-accent-900">
                  {diagLoading ? '…' : (diag?.total ?? 0).toLocaleString()}
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-2 text-xs">
              {diagLoading ? (
                <span className="text-foreground-400 flex items-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin"></i>Lecture des statistiques…
                </span>
              ) : trackingOk ? (
                <span className="text-accent-700 flex items-center gap-1.5">
                  <i className="ri-checkbox-circle-line"></i>
                  Le suivi est actif — le trafic remonte correctement.
                  {diag?.lastAt && (
                    <span className="text-foreground-500">
                      Dernière activité : {new Date(diag.lastAt).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-amber-700 flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  Aucune visite enregistrée pour le moment. Ouvrez votre site public pour générer les premières visites.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}